import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './Host.module.scss';
import { connect } from 'react-redux';
import classnames from 'classnames';

import Cheque from '../../util/Cheque/Cheque';
import BounceHelmet from '../../util/Helmet/Helmet';
import EventPreview from './EventPreview';
import moment from 'moment';
import { deleteBank } from 'legacy/actions/connectActions';
import { getUserHostedEvents } from 'legacy/actions/userActions';
import { Popup } from 'semantic-ui-react';
import { showModal } from 'legacy/actions/modalActions';
import Button from 'legacy/common/components/button/Button';
import Spinner from '../../util/Spinner/Spinner';
import { Event, User, Bank } from 'legacy/common/types/models';

import { NextRouter } from 'next/router';

interface StateProps {
  user: User & { hostedEvents?: Event[] };
  banks: Bank[];
  device: string;
  userLoading: { [key: string]: boolean };
}

interface DispatchProps {
  deleteBank: (bankId: string) => void;
  showModal: (modalType: string) => void;
  getUserHostedEvents: () => void;
}

interface OwnProps {
  router: NextRouter;
}

type HostProps = StateProps & DispatchProps & OwnProps;

function Host({
  router,
  banks,
  deleteBank,
  device,
  user,
  showModal,
  getUserHostedEvents,
  userLoading
}: HostProps) {
  const { t } = useTranslation(['events']);
  useEffect(() => {
    getUserHostedEvents();
  }, [user?._id]);

  const getHostEvent = (event) => {
    if (event.isOrganizer) {
      router.push(`/events/${event._id}/dashboard`);
    } else {
      router.push(`/events/${event._id}`);
    }
  };

  const openEventPlannerFlow = (event) => {
    router.push(`/events/${event._id}?view=edit`);
  };

  const bankComponents = banks.map((bank) => (
    <Cheque
      key={bank._id}
      bank={bank}
      handleDelete={() => deleteBank(bank._id)}
    />
  ));

  const now = moment();
  const events = user?.hostedEvents || [];

  const pastEvents = events.filter((e) => {
    const start = moment(e.startDate);
    let end = start.clone();
    if (e.endDate && moment(e.endDate).isValid()) {
      end = moment(e.endDate);
    }
    return end.isBefore(now);
  });

  const upcomingEvents = events.filter((e) => {
    const start = moment(e.startDate);
    if (start.isSameOrAfter(now)) {
      return true;
    }

    if (e.endDate && moment(e.endDate).isValid()) {
      const end = moment(e.endDate);
      return end.isSameOrAfter(now);
    }

    return false;
  });

  const pastEventElements = pastEvents.map((event) => (
    <EventPreview
      key={event._id}
      event={event}
      onClick={() => getHostEvent(event)}
    />
  ));

  const upcomingEventElements = upcomingEvents.map((event) => (
    <EventPreview
      key={event._id}
      event={event}
      onClick={() =>
        event.isDraft ? openEventPlannerFlow(event) : getHostEvent(event)
      }
    />
  ));

  return (
    <div className={classnames([styles.Host], device)}>
      <BounceHelmet
        title={t('events:hostDashboard.title')}
        description={t('events:hostDashboard.description')}
      />
      <div className="host-wrapper">
        <div className="bn-container header">
          <Button
            className="bn-btn host-new-event"
            onClick={() => router.push('/events/create')}
          >
            {t('events:hostDashboard.hostNewEvent')}
          </Button>
        </div>

        <div className="col main-content">
          <div className="bn-container">
            <div className="title">
              {t('events:hostDashboard.bankAccounts')}{' '}
              <Popup
                trigger={<i className="fas fa-info-circle" />}
                content={t('events:hostDashboard.bankAccountTooltip')}
                position="right center"
              />
            </div>
            <div className="content">
              <div className="section payout-methods">
                <div className="banks">
                  {bankComponents}
                  {banks.length < 15 && (
                    <div
                      className="add-payout"
                      onClick={() => showModal('ADD_BANK')}
                    >
                      <div className="img-content" data-cy="add-bank-button" />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          {userLoading.GET_USER_HOSTED_EVENTS && (
            <div
              style={{
                height: '100%',
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}
            >
              <Spinner width={50} height={50} />
            </div>
          )}
          {upcomingEventElements.length > 0 && (
            <div className="bn-container">
              <h2 className="title">
                {t('events:hostDashboard.upcomingEvents')}
              </h2>
              <div className="content">
                <div className="section events">{upcomingEventElements}</div>
              </div>
            </div>
          )}
          {pastEventElements.length > 0 && (
            <div className="bn-container">
              <h2 className="title">{t('events:hostDashboard.pastEvents')}</h2>
              <div className="content">
                <div className="section events">{pastEventElements}</div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const mapStateToProps = (state: any) => ({
  user: state.user.user,
  banks: state.connect.banks,
  device: state.app.device,
  userLoading: state.user.loading
});

const actions = {
  deleteBank,
  showModal,
  getUserHostedEvents
};

export default connect(mapStateToProps, actions)(Host as any) as any;
